import { IntlErrorCode } from 'next-intl';
import type { IntlError } from 'next-intl';

// The shared locale files in `packages/locales` use i18next-style placeholders
// (`{{var}}`), but `next-intl` parses messages as ICU MessageFormat which
// expects single braces (`{var}`). Without this conversion, ICU treats the
// strings as invalid and the raw template leaks through to the UI.
const MUSTACHE_PLACEHOLDER_RE = /\{\{\s*(\w+)\s*\}\}/g;

export const convertMustachePlaceholders = <T>(input: T): T => {
  if (typeof input === 'string') {
    return input.replace(MUSTACHE_PLACEHOLDER_RE, '{$1}') as unknown as T;
  }
  if (Array.isArray(input)) {
    return input.map(convertMustachePlaceholders) as unknown as T;
  }
  if (input && typeof input === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(input as Record<string, unknown>)) {
      out[key] = convertMustachePlaceholders((input as Record<string, unknown>)[key]);
    }
    return out as unknown as T;
  }
  return input;
};

export const getNestedMessage = (
  messages: Record<string, any>,
  path: string,
): string => {
  const parts = path.split('.');
  let current: any = messages;
  for (const part of parts) {
    if (current == null) return String(path);
    current = current[part];
  }

  return String(current);
};

/**
 * The portal formats no dates through next-intl, so this only pins the value
 * next-intl needs on both sides of a server render to be sure of matching markup.
 */
export const INTL_TIME_ZONE = 'UTC';

export const handleIntlError = (error: IntlError): void => {
  if (error.code === IntlErrorCode.INVALID_MESSAGE) return;
  console.warn(error);
};

export const createMessageFallback = (
  messages: Record<string, any>,
): (({ namespace, key, error }: { namespace?: string; key: string; error: IntlError }) => string) => {
  return ({ namespace, key, error }): string => {
    const path = [namespace, key].filter(Boolean).join('.');
    if (error.code === IntlErrorCode.INVALID_MESSAGE) {
      return getNestedMessage(messages, path);
    }

    return path;
  };
};
